import {
  Observable,
  ObservableNotification,
  OperatorFunction,
  pipe,
  UnaryFunction,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  materialize,
  scan,
  startWith,
} from 'rxjs/operators';

interface PendingAction {
  type: 'pending';
}
interface ResultAction<T> {
  type: 'result';
  payload: T;
}

export interface Task<T, E = unknown> {
  running: boolean;
  result: T | null;
  error: E | null;
}

type ActionNotification<T> = ObservableNotification<
  PendingAction | ResultAction<T>
>;

const taskReducer = <T, E>(
  state: Task<T, E>,
  notification: ActionNotification<T>
): Task<T, E> => {
  if (notification.kind === 'C') {
    return state.running ? { ...state, running: false } : state;
  }

  if (notification.kind === 'E') {
    return { running: false, result: null, error: notification.error };
  }

  const action = notification.value;
  if (action?.type === 'pending') {
    return { ...state, running: true, error: null };
  }

  if (action?.type === 'result') {
    return { running: false, result: action.payload, error: null };
  }

  return state;
};

const toResult = <T>(payload: T): ResultAction<T> => ({
  type: 'result',
  payload,
});
const pending: PendingAction = { type: 'pending' };

export const asyncTask = <T, E = unknown>(
  worker: Observable<T>
): Observable<Task<T, E>> => {
  const initial: Task<T, E> = { running: false, result: null, error: null };
  return worker.pipe(
    map(toResult),
    startWith(pending),
    materialize(),
    scan(taskReducer, initial),
    distinctUntilChanged()
  );
};

export const convertToAsyncTaskState =
  <T, E = unknown>(): OperatorFunction<T, Task<T, E>> =>
  (source) =>
    asyncTask<T, E>(source);

export const keepPreviousResult =
  <T extends Task<unknown>>(): OperatorFunction<T, T> =>
  (source) =>
    source.pipe(
      scan(({ result }, next) => (next.running ? { ...next, result } : next))
    );

/**
 * filters and lets through only non 'loading' non Error values from responseState operator.
 * so basically only allows successfull values to be emited further
 */
export const onlyResult = <T, E = unknown>(): UnaryFunction<
  Observable<Task<T, E>>,
  Observable<T>
> =>
  pipe(
    filter(
      ({ result, running, error }) => result != null && !running && !error
    ),
    map((res) => res.result as T)
  );
